// src/utils/xlsxReport.js
import * as XLSX from "xlsx-js-style";

// ---------- helpers ----------
function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatWhen(isoOrDate) {
  const d = isoOrDate instanceof Date ? isoOrDate : safeDate(isoOrDate);
  if (!d) return "";
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function secondsBetweenISO(aIso, bIso) {
  const a = safeDate(aIso)?.getTime();
  const b = safeDate(bIso)?.getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.max(0, Math.floor((b - a) / 1000));
}

function pickNestedIso(ro, paths) {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = ro;

    for (const part of parts) {
      cur = cur?.[part];
    }

    if (cur) return cur;
  }

  return "";
}

function pickIso(ro, keys) {
  for (const k of keys) {
    const v = ro?.[k];

    if (v) return v;
  }

  return "";
}

function roCreatedISO(ro) {
  return (
    pickNestedIso(ro, [
      "timestamps.createdAt",
      "timestamps.openedAt",
      "timestamps.inAt",
      "timestamps.arrivedAt",
    ]) ||
    pickIso(ro, [
      "createdAtISO",
      "createdAtIso",
      "createdAtUtc",
      "createdAt",
      "openedAtISO",
      "openedAt",
      "inAt",
      "arrivedAt",
    ]) ||
    ""
  );
}

function roAssignedISO(ro) {
  return (
    pickNestedIso(ro, ["timestamps.assignedAt"]) ||
    pickIso(ro, ["assignedAtISO", "assignedAt"]) ||
    ""
  );
}

function roWorkStartISO(ro) {
  return (
    pickNestedIso(ro, ["timestamps.workStartAt"]) ||
    pickIso(ro, ["workStartAtISO", "workStartAt"]) ||
    ""
  );
}

function roWorkEndISO(ro) {
  return (
    pickNestedIso(ro, ["timestamps.workEndAt"]) ||
    pickIso(ro, ["workEndAtISO", "workEndAt"]) ||
    ""
  );
}

function roCompletedISO(ro) {
  return (
    pickNestedIso(ro, ["timestamps.completedAt"]) ||
    pickIso(ro, ["completedAtISO", "completedAt"]) ||
    ""
  );
}

function getAsOfDate(asOf) {
  return asOf instanceof Date && !Number.isNaN(asOf.getTime())
    ? asOf
    : safeDate(asOf) || new Date();
}

function ageMsFromCreated(ro, asOf = new Date()) {
  const created = safeDate(roCreatedISO(ro));
  if (!created) return null;

  const end = getAsOfDate(asOf);
  const diff = end.getTime() - created.getTime();

  if (!Number.isFinite(diff)) return null;

  return Math.max(0, diff);
}

function formatAgeFromMs(ms) {
  if (ms == null) return "";

  const totalMinutes = Math.max(0, Math.round(ms / 60000));

  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (totalHours < 24) {
    return remainingMinutes
      ? `${totalHours}h ${remainingMinutes}m`
      : `${totalHours}h`;
  }

  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;

  return remainingHours ? `${days}d ${remainingHours}h` : `${days}d`;
}

function ageDays(ro, asOf = new Date()) {
  const ms = ageMsFromCreated(ro, asOf);
  if (ms == null) return "";

  return Number((ms / 86400000).toFixed(1));
}

function ageLabel(ro, asOf = new Date()) {
  return formatAgeFromMs(ageMsFromCreated(ro, asOf));
}

function createdDateLabel(ro) {
  return formatWhen(roCreatedISO(ro));
}

function isOpenNow(ro) {
  const st = String(ro?.status || "").toUpperCase();
  if (st === "ARCHIVED") return false;
  if (st === "COMPLETED") return false;
  // fallback: treat as open if no completed timestamp
  return !safeDate(roCompletedISO(ro));
}

function computeRoMetrics(ro) {
  const created = roCreatedISO(ro);
  const assigned = roAssignedISO(ro);
  const workStart = roWorkStartISO(ro);
  const workEnd = roWorkEndISO(ro);
  const completed = roCompletedISO(ro);

  const secCreatedToAssigned = secondsBetweenISO(created, assigned);
  const secAssignedToCompleted = secondsBetweenISO(assigned, completed);
  const secWorked = secondsBetweenISO(workStart, workEnd);

  const partsReq = ro?.partsActionRequestedAt || "";
  const partsAct = ro?.partsActionedAt || "";
  const secPartsTurnaround = secondsBetweenISO(partsReq, partsAct);

  const flagged = safeNum(ro?.flaggedHours);
  const workedHours = secWorked != null ? secWorked / 3600 : 0;
  const efficiency = workedHours > 0 ? flagged / workedHours : null;

  return {
    created,
    assigned,
    workStart,
    workEnd,
    completed,
    secCreatedToAssigned,
    secAssignedToCompleted,
    secWorked,
    secPartsTurnaround,
    flagged,
    workedHours,
    efficiency,
  };
}

function downloadArrayBufferAsFile(arrayBuffer, filename) {
  const blob = new Blob([arrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------- styling ----------
const HEADER_STYLE = {
  font: { bold: true, color: { rgb: "FFFFFF" } },
  fill: { patternType: "solid", fgColor: { rgb: "111827" } }, // dark slate
  alignment: { vertical: "center", horizontal: "center", wrapText: true },
  border: {
    top: { style: "thin", color: { rgb: "E5E7EB" } },
    bottom: { style: "thin", color: { rgb: "E5E7EB" } },
    left: { style: "thin", color: { rgb: "E5E7EB" } },
    right: { style: "thin", color: { rgb: "E5E7EB" } },
  },
};

const CELL_STYLE = {
  alignment: { vertical: "top", wrapText: true },
};

function applySheetBasics(ws, columns, { freezeTopRow = true, autoFilter = true } = {}) {
  // Column widths
  ws["!cols"] = (columns || []).map((c) => ({
    wch: c.wch || Math.min(40, Math.max(10, (c.header?.length || 10) + 2)),
  }));

  // Freeze top row
  if (freezeTopRow) {
    ws["!freeze"] = {
      xSplit: 0,
      ySplit: 1,
      topLeftCell: "A2",
      activePane: "bottomLeft",
      state: "frozen",
    };
  }

  // AutoFilter
  if (autoFilter) {
    const endCol = XLSX.utils.encode_col((columns?.length || 1) - 1);
    ws["!autofilter"] = { ref: `A1:${endCol}1` };
  }

  // Style header + cells
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1:A1");
  for (let C = range.s.c; C <= range.e.c; C++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c: C });
    if (ws[addr]) ws[addr].s = HEADER_STYLE;
  }

  for (let R = 1; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (ws[addr]) ws[addr].s = CELL_STYLE;
    }
  }
}

function makeSheetFromColumns(rows, columns) {
  const header = columns.map((c) => c.header);
  const data = (rows || []).map((r) =>
    columns.map((c) => (typeof c.value === "function" ? c.value(r) : r?.[c.key] ?? ""))
  );
  const aoa = [header, ...data];
  return XLSX.utils.aoa_to_sheet(aoa);
}

// ---------- sheet builders ----------
function buildSummaryRows({ allRos = [], kpiRows = [], fromDateKey, toDateKey }) {
  const subset = Array.isArray(kpiRows) ? kpiRows : [];
  const openNow = Array.isArray(allRos) ? allRos.filter((r) => isOpenNow(r)).length : 0;

  const completedInRange = subset.filter((r) => !!safeDate(roCompletedISO(r))).length;
  const waitersInRange = subset.filter(
    (r) => String(r?.transportType || r?.transport || "").toUpperCase() === "WAITER"
  ).length;

  return [
    {
      range_from: fromDateKey || "",
      range_to: toDateKey || "",
      total_ros_created_in_range: subset.length,
      completed_in_range: completedInRange,
      waiters_in_range: waitersInRange,
      open_ros_now: openNow,
    },
  ];
}

function buildOpenNowRows({ allRos = [], asOf = new Date() }) {
  const open = (Array.isArray(allRos) ? allRos : []).filter((r) => isOpenNow(r));
  return open.map((ro) => ({
    roNumber: ro?.roNumber ?? ro?.ro ?? "",
    customerName: ro?.customerName ?? "",
    advisorName: ro?.advisorName ?? "",
    techName: ro?.techName ?? ro?.technicianName ?? ro?.assignedTechName ?? "",
    status: ro?.status ?? "",
    lifecycle: ro?.lifecycle ?? "",
    transportType: ro?.transportType ?? ro?.transport ?? "",
    partsStatus: ro?.partsStatus ?? "",
    promiseTime: formatWhen(ro?.promiseTimeISO || ro?.promiseTime || ro?.promiseAt),
    createdAt: createdDateLabel(ro),
    ageDays: ageDays(ro, asOf),
    age: ageLabel(ro, asOf),
    notes: ro?.notes ?? "",
  }));
}

function buildRoDetailRows({ kpiRows = [], asOf = new Date() }) {
  const subset = Array.isArray(kpiRows) ? kpiRows : [];
  return subset.map((ro) => {
    const m = computeRoMetrics(ro);
    const tech = ro?.techName ?? ro?.technicianName ?? ro?.assignedTechName ?? ro?.assignedTechId ?? "";

    return {
      roNumber: ro?.roNumber ?? ro?.ro ?? "",
      vin: ro?.vin ?? "",
      customerName: ro?.customerName ?? "",
      advisorName: ro?.advisorName ?? "",
      techName: tech,
      status: ro?.status ?? "",
      lifecycle: ro?.lifecycle ?? "",
      transportType: ro?.transportType ?? ro?.transport ?? "",
      partsStatus: ro?.partsStatus ?? "",
      promiseTime: formatWhen(ro?.promiseTimeISO || ro?.promiseTime || ro?.promiseAt),

      createdAt: formatWhen(m.created),
      ageDays: ageDays(ro, asOf),
      age: ageLabel(ro, asOf),
      assignedAt: formatWhen(m.assigned),
      completedAt: formatWhen(m.completed),

      dispatchToAssign_min: m.secCreatedToAssigned == null ? "" : (m.secCreatedToAssigned / 60).toFixed(1),
      assignToComplete_min: m.secAssignedToCompleted == null ? "" : (m.secAssignedToCompleted / 60).toFixed(1),

      workStartAt: formatWhen(m.workStart),
      workEndAt: formatWhen(m.workEnd),
      techWorked_hrs: m.secWorked == null ? "" : m.workedHours.toFixed(2),

      flaggedHours: m.flagged.toFixed(1),
      techEfficiency_flagged_per_worked: m.efficiency == null ? "" : m.efficiency.toFixed(2),

      partsRequestedAt: formatWhen(ro?.partsActionRequestedAt),
      partsActionedAt: formatWhen(ro?.partsActionedAt),
      partsTurnaround_min: m.secPartsTurnaround == null ? "" : (m.secPartsTurnaround / 60).toFixed(1),

      notes: ro?.notes ?? "",
    };
  });
}

function buildTechEfficiencyRows({ kpiRows = [] }) {
  const subset = (Array.isArray(kpiRows) ? kpiRows : []).filter((ro) => !!safeDate(roCompletedISO(ro)));

  const byTech = new Map();
  subset.forEach((ro) => {
    const tech = ro?.techName ?? ro?.technicianName ?? ro?.assignedTechName ?? ro?.assignedTechId ?? "Unassigned";
    const m = computeRoMetrics(ro);

    if (!byTech.has(tech)) byTech.set(tech, { techName: tech, completedCount: 0, totalFlagged: 0, totalWorked: 0 });
    const t = byTech.get(tech);
    t.completedCount += 1;
    t.totalFlagged += safeNum(m.flagged);
    t.totalWorked += safeNum(m.workedHours);
  });

  return Array.from(byTech.values())
    .map((t) => {
      const eff = t.totalWorked > 0 ? t.totalFlagged / t.totalWorked : null;
      return {
        techName: t.techName,
        completedCount: t.completedCount,
        totalFlaggedHours: t.totalFlagged.toFixed(1),
        totalWorkedHours: t.totalWorked.toFixed(2),
        efficiency_flagged_per_worked: eff == null ? "" : eff.toFixed(2),
      };
    })
    .sort((a, b) => Number(b.efficiency_flagged_per_worked || 0) - Number(a.efficiency_flagged_per_worked || 0));
}

function buildPartsTimingRows({ kpiRows = [], asOf = new Date() }) {
  const subset = Array.isArray(kpiRows) ? kpiRows : [];
  return subset
    .filter((ro) => ro?.partsActionRequestedAt || ro?.partsActionedAt)
    .map((ro) => {
      const m = computeRoMetrics(ro);
      return {
        roNumber: ro?.roNumber ?? "",
        createdAt: createdDateLabel(ro),
        ageDays: ageDays(ro, asOf),
        age: ageLabel(ro, asOf),
        advisorName: ro?.advisorName ?? "",
        techName: ro?.techName ?? ro?.technicianName ?? ro?.assignedTechName ?? "",
        partsStatus: ro?.partsStatus ?? "",
        partsRequestedAt: formatWhen(ro?.partsActionRequestedAt),
        partsActionedAt: formatWhen(ro?.partsActionedAt),
        partsTurnaround_min: m.secPartsTurnaround == null ? "" : (m.secPartsTurnaround / 60).toFixed(1),
      };
    });
}

// ---------- columns (nice headers + widths) ----------
const SUMMARY_COLS = [
  { key: "range_from", header: "Range From", wch: 14 },
  { key: "range_to", header: "Range To", wch: 14 },
  { key: "total_ros_created_in_range", header: "ROs Created (In Range)", wch: 22 },
  { key: "completed_in_range", header: "Completed (In Range)", wch: 20 },
  { key: "waiters_in_range", header: "Waiters (In Range)", wch: 18 },
  { key: "open_ros_now", header: "Open ROs (Now)", wch: 16 },
];

const OPEN_NOW_COLS = [
  { key: "roNumber", header: "RO Number", wch: 12 },
  { key: "customerName", header: "Customer", wch: 22 },
  { key: "advisorName", header: "Advisor", wch: 18 },
  { key: "techName", header: "Technician", wch: 18 },
  { key: "status", header: "Status", wch: 16 },
  { key: "lifecycle", header: "Lifecycle", wch: 18 },
  { key: "transportType", header: "Transport", wch: 12 },
  { key: "partsStatus", header: "Parts Status", wch: 14 },
  { key: "promiseTime", header: "Promise Time", wch: 22 },
  { key: "createdAt", header: "Created Date", wch: 22 },
  { key: "ageDays", header: "Age Days", wch: 12 },
  { key: "age", header: "Age", wch: 14 },
  { key: "notes", header: "Notes", wch: 40 },
];

const DETAIL_COLS = [
  { key: "roNumber", header: "RO Number", wch: 12 },
  { key: "vin", header: "VIN", wch: 20 },
  { key: "customerName", header: "Customer", wch: 22 },
  { key: "advisorName", header: "Advisor", wch: 16 },
  { key: "techName", header: "Technician", wch: 16 },
  { key: "status", header: "Status", wch: 16 },
  { key: "lifecycle", header: "Lifecycle", wch: 18 },
  { key: "transportType", header: "Transport", wch: 12 },
  { key: "partsStatus", header: "Parts Status", wch: 14 },
  { key: "promiseTime", header: "Promise Time", wch: 22 },
  { key: "createdAt", header: "Created Date", wch: 22 },
  { key: "ageDays", header: "Age Days", wch: 12 },
  { key: "age", header: "Age", wch: 14 },
  { key: "assignedAt", header: "Assigned At", wch: 22 },
  { key: "completedAt", header: "Completed At", wch: 22 },
  { key: "dispatchToAssign_min", header: "Dispatch → Assign (min)", wch: 22 },
  { key: "assignToComplete_min", header: "Assign → Complete (min)", wch: 22 },
  { key: "workStartAt", header: "Work Start", wch: 20 },
  { key: "workEndAt", header: "Work End", wch: 20 },
  { key: "techWorked_hrs", header: "Tech Worked (hrs)", wch: 18 },
  { key: "flaggedHours", header: "Flagged Hours", wch: 14 },
  { key: "techEfficiency_flagged_per_worked", header: "Tech Efficiency (Flagged/Worked)", wch: 30 },
  { key: "partsRequestedAt", header: "Parts Requested At", wch: 22 },
  { key: "partsActionedAt", header: "Parts Actioned At", wch: 22 },
  { key: "partsTurnaround_min", header: "Parts Turnaround (min)", wch: 22 },
  { key: "notes", header: "Notes", wch: 40 },
];

const TECH_COLS = [
  { key: "techName", header: "Technician", wch: 22 },
  { key: "completedCount", header: "Completed ROs", wch: 14 },
  { key: "totalFlaggedHours", header: "Total Flagged Hours", wch: 18 },
  { key: "totalWorkedHours", header: "Total Worked Hours", wch: 18 },
  { key: "efficiency_flagged_per_worked", header: "Efficiency (Flagged/Worked)", wch: 26 },
];

const PARTS_COLS = [
  { key: "roNumber", header: "RO Number", wch: 12 },
  { key: "createdAt", header: "Created Date", wch: 22 },
  { key: "ageDays", header: "Age Days", wch: 12 },
  { key: "age", header: "Age", wch: 14 },
  { key: "advisorName", header: "Advisor", wch: 18 },
  { key: "techName", header: "Technician", wch: 18 },
  { key: "partsStatus", header: "Parts Status", wch: 14 },
  { key: "partsRequestedAt", header: "Parts Requested At", wch: 22 },
  { key: "partsActionedAt", header: "Parts Actioned At", wch: 22 },
  { key: "partsTurnaround_min", header: "Parts Turnaround (min)", wch: 22 },
];

// ---------- public exports ----------
export async function exportManagerReportXlsx({
  allRos = [],
  kpiRows = [],
  fromDateKey,
  toDateKey,
  filenamePrefix = "flowtrak_manager",
}) {
  const wb = XLSX.utils.book_new();
  const asOf = new Date();

  // Summary
  const summaryRows = buildSummaryRows({ allRos, kpiRows, fromDateKey, toDateKey });
  const wsSummary = makeSheetFromColumns(summaryRows, SUMMARY_COLS);
  applySheetBasics(wsSummary, SUMMARY_COLS);
  XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

  // Always include Open Now (so managers never see “blank export”)
  const openNowRows = buildOpenNowRows({ allRos, asOf });
  const wsOpen = makeSheetFromColumns(openNowRows, OPEN_NOW_COLS);
  applySheetBasics(wsOpen, OPEN_NOW_COLS);
  XLSX.utils.book_append_sheet(wb, wsOpen, "Open ROs Now");

  // Range detail
  const detailRows = buildRoDetailRows({ kpiRows, asOf });
  const wsDetail = makeSheetFromColumns(detailRows, DETAIL_COLS);
  applySheetBasics(wsDetail, DETAIL_COLS);
  XLSX.utils.book_append_sheet(wb, wsDetail, "RO Detail");

  // Tech efficiency (only completed in range)
  const techRows = buildTechEfficiencyRows({ kpiRows });
  const wsTech = makeSheetFromColumns(techRows, TECH_COLS);
  applySheetBasics(wsTech, TECH_COLS);
  XLSX.utils.book_append_sheet(wb, wsTech, "Tech Efficiency");

  // Parts timing (only where parts timestamps exist)
  const partsRows = buildPartsTimingRows({ kpiRows, asOf });
  const wsParts = makeSheetFromColumns(partsRows, PARTS_COLS);
  applySheetBasics(wsParts, PARTS_COLS);
  XLSX.utils.book_append_sheet(wb, wsParts, "Parts Timing");

  const from = fromDateKey || "start";
  const to = toDateKey || "end";
  const filename = `${filenamePrefix}_${from}_to_${to}.xlsx`;

  const arrayBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  downloadArrayBufferAsFile(arrayBuffer, filename);
}

export async function exportWipFilteredXlsx({ wipRows = [], dateLabel, filenamePrefix = "flowtrak_wip" }) {
  const wb = XLSX.utils.book_new();
  const asOf = new Date();

  const rows = (Array.isArray(wipRows) ? wipRows : []).map((ro) => ({
    roNumber: ro?.roNumber ?? ro?.ro ?? "",
    vin: ro?.vin ?? "",
    customerName: ro?.customerName ?? "",
    advisorName: ro?.advisorName ?? "",
    techName: ro?.techName ?? ro?.technicianName ?? ro?.assignedTechName ?? "",
    status: ro?.status ?? "",
    lifecycle: ro?.lifecycle ?? "",
    transportType: ro?.transportType ?? ro?.transport ?? "",
    partsStatus: ro?.partsStatus ?? "",
    promiseTime: formatWhen(ro?.promiseTimeISO || ro?.promiseTime || ro?.promiseAt),
    createdAt: createdDateLabel(ro),
    ageDays: ageDays(ro, asOf),
    age: ageLabel(ro, asOf),
    updatedAt: formatWhen(ro?.timestamps?.updatedAt || ro?.updatedAtISO || ro?.updatedAt),
    notes: ro?.notes ?? "",
  }));

  const cols = [
    { key: "roNumber", header: "RO Number", wch: 12 },
    { key: "vin", header: "VIN", wch: 20 },
    { key: "customerName", header: "Customer", wch: 22 },
    { key: "advisorName", header: "Advisor", wch: 18 },
    { key: "techName", header: "Technician", wch: 18 },
    { key: "status", header: "Status", wch: 16 },
    { key: "lifecycle", header: "Lifecycle", wch: 18 },
    { key: "transportType", header: "Transport", wch: 12 },
    { key: "partsStatus", header: "Parts Status", wch: 14 },
    { key: "promiseTime", header: "Promise Time", wch: 22 },
    { key: "createdAt", header: "Created Date", wch: 22 },
    { key: "ageDays", header: "Age Days", wch: 12 },
    { key: "age", header: "Age", wch: 14 },
    { key: "updatedAt", header: "Updated", wch: 22 },
    { key: "notes", header: "Notes", wch: 40 },
  ];

  const ws = makeSheetFromColumns(rows, cols);
  applySheetBasics(ws, cols);
  XLSX.utils.book_append_sheet(wb, ws, "WIP");

  const dk = dateLabel || "";
  const filename = `${filenamePrefix}_${dk}_filtered.xlsx`;
  const arrayBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  downloadArrayBufferAsFile(arrayBuffer, filename);
}